import * as React from "react";
import axios from "axios";
import { shortHash } from "../ui";

const PAGE_SIZES = [10, 20, 50];

function formatTime(unix?: number | null) {
  if (!unix) return "—";
  try {
    return new Date(unix * 1000).toLocaleString();
  } catch {
    return "—";
  }
}

function PartyList({
  parties,
  emptyLabel,
}: {
  parties: any[];
  emptyLabel: string;
}) {
  if (!parties?.length) {
    return <span className="dim">{emptyLabel}</span>;
  }
  return (
    <div className="party-list">
      {parties.map((p, i) => {
        if (p.coinbase) {
          return (
            <span key={i} className="tag">
              Coinbase
            </span>
          );
        }
        return (
          <a
            key={p.address + i}
            className="mono party-link"
            href={
              "index.html?route=ADDRESS&address=" + encodeURIComponent(p.address)
            }
            title={p.address}
          >
            {shortHash(p.address, 6)}
          </a>
        );
      })}
    </div>
  );
}

export function History({ address }: { address: string | null }) {
  const [limit, setLimit] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState<any>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setPage(1);
  }, [address, limit]);

  React.useEffect(() => {
    if (!address) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get("/api/addresshistory/" + encodeURIComponent(address), {
        params: { limit, page },
      })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [address, limit, page]);

  if (!address) return <span className="dim">No address</span>;
  if (loading && !data) return <span className="dim">Loading history…</span>;
  if (error) return <span className="dim">Failed to load history: {error}</span>;
  if (!data) return <span className="dim">No history</span>;

  const items: any[] = data.items || [];
  const hasMore = !!data.hasMore;
  const maxKnown = hasMore ? page + 2 : page;
  const pageButtons: number[] = [];
  for (let p = 1; p <= Math.max(maxKnown, page); p++) pageButtons.push(p);

  return (
    <div className="history">
      <div className="history-toolbar">
        <div className="row">
          <span className="dim">Show</span>
          {PAGE_SIZES.map((n) => (
            <button
              key={n}
              type="button"
              className={"btn btn-ghost" + (limit === n ? " active-page" : "")}
              onClick={() => setLimit(n)}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="pager">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Prev
          </button>
          {pageButtons.map((p) => (
            <button
              key={p}
              type="button"
              className={p === page ? "active-page" : ""}
              disabled={loading}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            disabled={!hasMore || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      </div>

      {loading ? <p className="dim">Updating…</p> : null}

      {!items.length ? (
        <span className="dim">No history in scanned range</span>
      ) : (
        <table className="data history-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Tx</th>
              <th>Amount</th>
              <th>From</th>
              <th>To</th>
              <th>Height</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const txHref =
                "index.html?route=TRANSACTION&id=" +
                encodeURIComponent(item.txid);
              const amtClass =
                item.direction === "in"
                  ? "amt-in"
                  : item.direction === "out"
                    ? "amt-out"
                    : "dim";
              const sign =
                item.direction === "in"
                  ? "+"
                  : item.direction === "out"
                    ? "−"
                    : "";
              return (
                <tr key={item.txid}>
                  <td className="nowrap">{formatTime(item.time)}</td>
                  <td>
                    <a className="mono" href={txHref} title={item.txid}>
                      {shortHash(item.txid, 8)}
                    </a>
                  </td>
                  <td className={"nowrap " + amtClass}>
                    {sign}
                    {Math.abs(item.amount).toLocaleString(undefined, {
                      maximumFractionDigits: 8,
                    })}{" "}
                    {item.assetName}
                  </td>
                  <td>
                    <PartyList
                      parties={item.from}
                      emptyLabel={
                        item.direction === "in" ? "—" : "self / unknown"
                      }
                    />
                  </td>
                  <td>
                    <PartyList
                      parties={item.to}
                      emptyLabel={
                        item.direction === "out" ? "—" : "self / unknown"
                      }
                    />
                  </td>
                  <td>
                    <a
                      className="mono"
                      href={
                        "index.html?route=BLOCK&hash=" +
                        encodeURIComponent(String(item.height))
                      }
                    >
                      {item.height.toLocaleString()}
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <p className="dim history-meta">
        Page {page} · {limit} per page
        {hasMore ? " · more available" : " · end of scanned history"}
        {loading ? " · loading…" : ""}
      </p>
    </div>
  );
}
